import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, MessageSquare, Frown, ChartNoAxesCombined, Target } from 'lucide-react';
import SectionTitle from './text_components/SectionTitle';

// Import Formspree
import { useForm } from '@formspree/react';

const FeedbackPage = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [otherInputs, setOtherInputs] = useState({});

  const [state, handleSubmitFormspree] = useForm("mqakrjwb");

  const questions = [
    {
      id: 1,
      category: "General",
      question: "How dissapointed would you be if you were not able to use PlanSync AI anymore?",
      options: [
        { text: "Extremely Not Dissapointed", value: 1 },
        { text: "Not Dissapointed", value: 2 },
        { text: "Neutral", value: 3 },
        { text: "Dissapointed", value: 4 },
        { text: "Extremely Dissapointed", value: 5 },
        { text: "Other", value: "other", hasInput: true },
      ],
      icon: <Target className="h-6 w-6 text-white" />,
    },
    {
      id: 2,
      category: "Goal Alignment",
      question: "What is the main reason you use PlanSync AI?",
      options: [
        { text: "Organizing all of my plan information", value: 1 },
        { text: "Automating Reporting", value: 2 },
        { text: "Keep track of compliance requirements", value: 3 },
        { text: "Tracking my plan performance", value: 4 },
        { text: "Grow my individual wealth advisory practice", value: 5 },
        { text: "Growing my 401k plan management business", value: 6 },
        { text: "Saving time", value: 7 },
        { text: "Other", value: "other", hasInput: true },
      ],
      icon: <Target className="h-6 w-6 text-white" />,
    },
    {
      id: 3,
      category: "Difficulty of Use",
      question: "How difficult is it to navigate and use our product's core features?",
      options: [
        { text: "Very Difficult", value: 1 },
        { text: "Difficult", value: 2 },
        { text: "Neutral", value: 3 },
        { text: "Not Difficult", value: 4 },
        { text: "Very not difficult", value: 5 },
        { text: "Other", value: "other", hasInput: true },
      ],
      icon: <Frown className="h-6 w-6 text-white" />,
    },
    {
      id: 4,
      category: "Next Improvement",
      question: "What should we prioritize improving in the next development sprint?",
      options: [
        { text: "UI/UX Design Enhancements (How pretty it looks)", value: "ui_ux" },
        { text: "Performance & Speed (How fast it is)", value: "performance" },
        { text: "New Feature Rollout (We need to add new features)", value: "new_feature" },
        { text: "Better Integrations (How well it integrates with other tools such as Salesforce, CRMs, etc.)", value: "better_integrations" },
        { text: "More Educational Resources (How much we educate you about how to use the product)", value: "education" },
        { text: "Other", value: "other", hasInput: true },
      ],
      icon: <ChartNoAxesCombined className="h-6 w-6 text-white" />,
    },
    {
      id: 5,
      category: "Open Feedback",
      question: "Any additional feedback, suggestions, or concerns?",
      type: "textarea",
      icon: <MessageSquare className="h-6 w-6 text-white" />,
    },
  ];

  const handleAnswer = (questionId, value) => {
    setResponses({ ...responses, [questionId]: value });

    if (value !== 'other' && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleTextAreaChange = (questionId, value) => {
    setResponses({ ...responses, [questionId]: value });
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleLocalSubmit = () => {
    if (Object.keys(responses).length === questions.length) {
      const final = { ...responses };
      Object.keys(responses).forEach(questionId => {
        if (responses[questionId] === 'other') {
          final[questionId] = {
            selection: 'other',
            customResponse: otherInputs[questionId] || ''
          };
        }
      });

      // Create form data with question text as field names
      const formData = new FormData();
      Object.keys(final).forEach((key) => {
        // Find the corresponding question
        const question = questions.find(q => q.id === parseInt(key));
        const value = final[key];
        const fieldValue = typeof value === 'object' && value.selection === 'other'
          ? `Other: ${value.customResponse}`
          : value;
        
        // Use question text as the field name
        formData.append(question.question, fieldValue);
      });
      
      handleSubmitFormspree(formData);
      setShowResult(true);
    } else {
      alert("Please answer all questions before submitting.");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  const questionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.5
      }
    },
    exit: { 
      opacity: 0, 
      y: -50,
      transition: { 
        duration: 0.2
      }
    }
  };

  const renderQuestionnaire = () => {
    const currentQuestion = questions[currentQuestionIndex];

    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 font-sans bg-white shadow-xl rounded-3xl mt-8 mb-8 border border-blue-300"
      >
        <motion.div variants={itemVariants}>
          <SectionTitle className="text-2xl sm:text-3xl font-bold text-blue-600">Your Feedback Matters</SectionTitle>
        </motion.div>

        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
          <div 
            className="bg-gradient-to-bl from-sky-400 to-blue-800 h-2.5 rounded-full transition-all duration-500 ease-in-out" 
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          ></div>
        </div>

        <AnimatePresence mode="wait">
          <motion.section
            key={currentQuestionIndex}
            variants={questionVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white rounded-2xl shadow-lg overflow-hidden"
          >
            <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-4 shadow-sm">
              <h2 className="text-xl font-semibold text-white flex items-center">
                {currentQuestion.icon}
                <span className="ml-2">{currentQuestion.category}</span>
              </h2>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="font-medium mb-3 text-gray-800">{currentQuestion.question}</p>
                {currentQuestion.type === 'textarea' ? (
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded-md"
                    rows="4"
                    value={responses[currentQuestion.id] || ''}
                    onChange={(e) => handleTextAreaChange(currentQuestion.id, e.target.value)}
                  />
                ) : (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, index) => (
                      <div key={index}>
                        <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg transition-all duration-200 hover:bg-blue-50">
                          <input
                            type="radio"
                            name={`question-${currentQuestion.id}`}
                            value={option.value}
                            checked={responses[currentQuestion.id] === option.value}
                            onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                            className="form-radio h-5 w-5 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">{option.text}</span>
                        </label>
                        {option.hasInput && responses[currentQuestion.id] === 'other' && (
                          <div className="ml-8 mt-2">
                            <input
                              type="text"
                              placeholder="Please specify..."
                              className="w-full p-2 border border-gray-300 rounded-md"
                              value={otherInputs[currentQuestion.id] || ''}
                              onChange={(e) => {
                                setOtherInputs({
                                  ...otherInputs,
                                  [currentQuestion.id]: e.target.value
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.section>
        </AnimatePresence>

        <motion.div variants={itemVariants} className="flex justify-between mt-6">
          {currentQuestionIndex > 0 && (
            <button
              onClick={handlePrevious}
              className="px-6 py-2 rounded-full text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              <ArrowLeft className="h-5 w-5 inline mr-2" />
              Previous
            </button>
          )}
          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={handleLocalSubmit}
              className="px-6 py-2 rounded-full text-white bg-gradient-to-bl from-sky-400 to-blue-800 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              Submit
              <ArrowRight className="h-5 w-5 inline ml-2" />
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
              className="px-6 py-2 rounded-full text-white bg-gradient-to-bl from-sky-400 to-blue-800 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              Next
              <ArrowRight className="h-5 w-5 inline ml-2" />
            </button>
          )}
        </motion.div>
      </motion.div>
    );
  };

  const renderResult = () => {
    // If submission to Formspree succeeded
    if (state.succeeded) {
      return (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 font-sans bg-white shadow-xl rounded-3xl mt-8 mb-8"
        >
          <motion.div variants={itemVariants}>
            <SectionTitle className="text-2xl sm:text-3xl font-bold text-blue-600">Thank You for Your Feedback!</SectionTitle>
          </motion.div>
          <motion.p variants={itemVariants} className="text-gray-700">
            We appreciate you taking the time to help us improve. Your insights will directly influence our next development sprint and guide us in better serving your needs.
          </motion.p>
          <motion.p variants={itemVariants} className="text-gray-700">
            If you have any further thoughts, feel free to reach out. We're committed to building a product that supports your success.
          </motion.p>
          <motion.div variants={itemVariants} className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Contact Information:</h3>
            <p className="text-gray-700">Email: help@401k-pro.ai</p>
            <p className="text-gray-700">Phone: (919) 200-9943</p>
            <p className="text-gray-700">Hours: 24/7/365 (We're a startup, so we're always working)</p>
          </motion.div>
        </motion.div>
      );
    }

    // Show loading state while submitting
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 font-sans bg-white shadow-xl rounded-3xl mt-8 mb-8"
      >
        <motion.div variants={itemVariants}>
          <SectionTitle className="text-2xl sm:text-3xl font-bold text-blue-600">Submitting Your Feedback...</SectionTitle>
        </motion.div>
        <motion.p variants={itemVariants} className="text-gray-700">
          Please wait while we process your feedback.
        </motion.p>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <AnimatePresence mode="wait">
        {showResult ? renderResult() : renderQuestionnaire()}
      </AnimatePresence>
    </div>
  );
};

export default FeedbackPage;

